import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  FileSignature, Plus, Send, CheckCircle2, Clock, DollarSign, X, ExternalLink,
  Users, Copy, Check, BadgeDollarSign,
} from "lucide-react";

const STATUS_PILL = {
  draft:     "bg-neutral-100 border-neutral-300 text-neutral-700",
  sent:      "bg-amber-50 border-amber-300 text-amber-800",
  signed:    "bg-blue-50 border-blue-300 text-blue-800",
  active:    "bg-emerald-50 border-emerald-300 text-emerald-800",
  completed: "bg-neutral-900 border-neutral-900 text-white",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

export default function Contracts() {
  const [rows, setRows] = useState([]);
  const [packages, setPackages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ client_id: "", package_id: "", contractor_id: "", scope_notes: "", template_id: "" });
  const [busyId, setBusyId] = useState("");
  const [copied, setCopied] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: p }, { data: cl }, { data: co }, { data: t }] = await Promise.all([
        api.get("/contracts"),
        api.get("/service-packages"),
        api.get("/clients"),
        api.get("/contractors"),
        api.get("/contract-templates").catch(() => ({ data: { templates: [] } })),
      ]);
      setRows(c.contracts || []);
      setPackages(p.packages || []);
      setClients(cl.clients || cl || []);
      setContractors(co.contractors || []);
      setTemplates(t.templates || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) { setForm((f) => ({ ...f, template_id: "" })); return; }
    setForm((f) => ({ ...f, template_id: tid, package_id: t.package_id, scope_notes: t.scope_notes || "" }));
    toast.success(`Applied template "${t.name}"`);
  };

  const saveAsTemplate = async () => {
    if (!form.package_id) { toast.error("Pick a package first"); return; }
    const name = window.prompt("Save this scope as a template — name it:");
    if (!name) return;
    setSavingTemplate(true);
    try {
      await api.post("/contract-templates", {
        name, package_id: form.package_id, scope_notes: form.scope_notes,
      });
      toast.success("Template saved.");
      const { data: t } = await api.get("/contract-templates");
      setTemplates(t.templates || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingTemplate(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.package_id) { toast.error("Client + package required"); return; }
    try {
      await api.post("/contracts", {
        client_id: form.client_id,
        package_id: form.package_id,
        contractor_id: form.contractor_id || null,
        scope_notes: form.scope_notes,
      });
      toast.success("Contract drafted.");
      setCreating(false);
      setForm({ client_id: "", package_id: "", contractor_id: "", scope_notes: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const send = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/contracts/${id}/send`);
      toast.success(data.email_sent ? "Sent for client signature." : "Prepared — email failed, copy the link below.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(""); }
  };

  const complete = async (id) => {
    if (!window.confirm("Mark this contract as completed? Both sides will be closed out.")) return;
    setBusyId(id);
    try {
      await api.post(`/contracts/${id}/complete`);
      toast.success("Contract completed.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(""); }
  };

  const copyLink = (token) => {
    const base = (window.location.origin);
    const url = `${base}/sign/${token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(token);
      toast.success("Sign link copied.");
      setTimeout(() => setCopied(""), 1800);
    });
  };

  const kpis = {
    total: rows.length,
    active: rows.filter((r) => r.status === "active").length,
    revenue: rows.filter((r) => ["signed", "active", "completed"].includes(r.status))
      .reduce((s, r) => s + (r.sell_price || 0), 0),
    margin: rows.filter((r) => ["signed", "active", "completed"].includes(r.status))
      .reduce((s, r) => s + (r.margin || 0), 0),
  };

  return (
    <div className="p-8 max-w-7xl" data-testid="contracts-page">
      <Toaster position="top-right" richColors />
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><FileSignature className="w-3.5 h-3.5" /> Contracts</div>
          <h1 className="text-3xl font-bold text-neutral-900">Client ↔ ClientsOnRamp ↔ Contractor</h1>
          <p className="text-neutral-600 mt-2 max-w-2xl text-sm">
            When a client signs their services agreement, a back-to-back contractor
            agreement is auto-created and sent. Both PDFs are archived in Files & media.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/contractors" className="lf-btn-outline text-sm inline-flex items-center gap-2">
            <Users className="w-4 h-4" /> Contractors
          </Link>
          <Link to="/service-packages" className="lf-btn-outline text-sm inline-flex items-center gap-2">
            <DollarSign className="w-4 h-4" /> Pricing
          </Link>
          <button onClick={() => setCreating(true)} className="lf-btn-dark text-sm inline-flex items-center gap-2" data-testid="contracts-new-btn">
            <Plus className="w-4 h-4" /> New contract
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="Contracts" value={kpis.total} />
        <Kpi label="Active" value={kpis.active} accent="emerald" />
        <Kpi label="Booked revenue" value={`$${kpis.revenue.toLocaleString()}`} />
        <Kpi label="Booked margin"  value={`$${kpis.margin.toLocaleString()}`} accent="emerald" />
      </div>

      {loading ? (
        <div className="text-neutral-500 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-neutral-300 bg-white p-16 text-center">
          <FileSignature className="w-8 h-8 mx-auto mb-3 text-neutral-400" />
          <div className="font-display text-xl mb-1">No contracts yet</div>
          <p className="text-neutral-500 text-sm">Create one to send a client their services agreement.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((c) => {
            const ctr = c.contractor_contract;
            const isBusy = busyId === c.id;
            const pill = STATUS_PILL[c.status] || STATUS_PILL.draft;
            return (
              <div key={c.id} className="bg-white border border-neutral-200 hover:border-neutral-400 transition p-5" data-testid={`contract-${c.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-display text-lg font-bold">{c.package_name}</span>
                      <span className={`text-xs font-mono uppercase tracking-widest px-2 py-0.5 border ${pill}`}>{c.status}</span>
                    </div>
                    <div className="text-sm text-neutral-500 flex flex-wrap gap-x-4 gap-y-1">
                      <span><strong className="text-neutral-900">{c.client?.business_name || "Unknown client"}</strong> · {c.client?.email}</span>
                      <span>Contractor: <strong className="text-neutral-900">{c.contractor?.name || "— unassigned —"}</strong></span>
                      <span className="font-mono text-xs">Created {fmt(c.created_at)} · Deliver by {fmt(c.delivery_by)}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-3 text-sm max-w-xl">
                      <div><div className="overline">Sell</div><strong className="text-neutral-900">${c.sell_price.toLocaleString()}</strong></div>
                      <div><div className="overline">Payout</div><strong className="text-neutral-900">${c.contractor_payout.toLocaleString()}</strong></div>
                      <div><div className="overline">Margin</div><strong className="text-emerald-700">${c.margin.toLocaleString()}</strong></div>
                    </div>
                    {ctr && (
                      <div className="mt-3 text-xs text-neutral-500 border-l-2 border-blue-300 pl-3">
                        Contractor agreement · <span className={`inline-block ml-1 text-xs font-mono uppercase tracking-widest px-2 py-0.5 border ${STATUS_PILL[ctr.status] || STATUS_PILL.draft}`}>{ctr.status}</span>
                        {ctr.party_b_signed_at && <span className="ml-2">signed {fmt(ctr.party_b_signed_at)} by {ctr.party_b_signed_name}</span>}
                        {!ctr.party_b_signed_at && (
                          <button onClick={() => copyLink(ctr.sign_token)} className="ml-2 underline">
                            {copied === ctr.sign_token ? "copied!" : "copy contractor sign link"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    {c.status === "draft" && (
                      <button onClick={() => send(c.id)} disabled={isBusy} className="lf-btn-dark text-xs px-3 py-1.5 flex items-center gap-1" data-testid={`contract-send-${c.id}`}>
                        <Send className="w-3 h-3" /> Send to client
                      </button>
                    )}
                    {c.status === "sent" && (
                      <>
                        <button onClick={() => copyLink(c.sign_token)} className="lf-btn-outline text-xs px-3 py-1.5 flex items-center gap-1">
                          {copied === c.sign_token ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy sign link</>}
                        </button>
                        <button onClick={() => send(c.id)} disabled={isBusy} className="lf-btn-outline text-xs px-3 py-1.5 flex items-center gap-1">
                          <Send className="w-3 h-3" /> Resend
                        </button>
                      </>
                    )}
                    {c.status === "active" && (
                      <button onClick={() => complete(c.id)} disabled={isBusy} className="lf-btn-outline text-xs px-3 py-1.5 flex items-center gap-1" data-testid={`contract-complete-${c.id}`}>
                        <CheckCircle2 className="w-3 h-3" /> Mark completed
                      </button>
                    )}
                    {(c.status === "completed" || c.status === "active") && ctr && (
                      <PayoutToggle
                        contractId={c.id}
                        status={ctr.payout_status || "unpaid"}
                        onChanged={load}
                      />
                    )}
                    {c.pdf_file_id && (
                      <a href={`/api/files/${c.pdf_file_id}/download`} target="_blank" rel="noopener noreferrer" className="text-xs text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> PDF
                      </a>
                    )}
                    {["signed", "active", "completed"].includes(c.status) && (
                      <button
                        onClick={() => {
                          const url = `${window.location.origin}/status/${c.sign_token}`;
                          navigator.clipboard.writeText(url).then(() => {
                            setCopied(`status-${c.sign_token}`);
                            toast.success("Client status link copied.");
                            setTimeout(() => setCopied(""), 1800);
                          });
                        }}
                        className="text-xs text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1"
                        data-testid={`contract-status-link-${c.id}`}
                        title="Read-only progress page you can share with the client"
                      >
                        {copied === `status-${c.sign_token}` ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Client status link</>}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {creating && (
        <div onClick={() => setCreating(false)} className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="new-contract-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="bg-white w-full max-w-xl border border-neutral-900 shadow-2xl">
            <div className="bg-neutral-900 text-white px-6 py-4 flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-amber-300">New contract</div>
                <div className="text-xl font-bold mt-1">Draft a services agreement</div>
              </div>
              <button type="button" onClick={() => setCreating(false)} className="text-neutral-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 grid gap-4 text-sm">
              {templates.length > 0 && (
                <label className="grid gap-1">
                  <span className="overline">Load from template · optional</span>
                  <select value={form.template_id} onChange={(e) => applyTemplate(e.target.value)} data-testid="new-contract-template" className="border border-neutral-300 px-3 py-2 bg-amber-50">
                    <option value="">— start blank —</option>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </label>
              )}
              <label className="grid gap-1">
                <span className="overline">Client · required</span>
                <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="new-contract-client" className="border border-neutral-300 px-3 py-2">
                  <option value="">— pick a client —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.business_name || c.name}</option>)}
                </select>
              </label>
              <label className="grid gap-1">
                <span className="overline">Service package · required</span>
                <select required value={form.package_id} onChange={(e) => setForm({ ...form, package_id: e.target.value })} data-testid="new-contract-package" className="border border-neutral-300 px-3 py-2">
                  <option value="">— pick a package —</option>
                  {packages.map((p) => <option key={p.id} value={p.id}>{p.name} · ${p.sell_price} sell / ${p.contractor_payout} payout / {p.delivery_days}d</option>)}
                </select>
              </label>
              <label className="grid gap-1">
                <span className="overline">Assign contractor · optional</span>
                <select value={form.contractor_id} onChange={(e) => setForm({ ...form, contractor_id: e.target.value })} data-testid="new-contract-contractor" className="border border-neutral-300 px-3 py-2">
                  <option value="">— assign later —</option>
                  {contractors.filter((c) => c.status !== "inactive").map((c) => (
                    <option key={c.id} value={c.id}>{c.name} · {c.email}</option>
                  ))}
                </select>
                <span className="text-xs text-neutral-500">You can assign a contractor before or after the client signs. The contractor agreement auto-fires once assigned.</span>
              </label>
              <label className="grid gap-1">
                <span className="overline">Scope notes · optional</span>
                <textarea rows={3} value={form.scope_notes} onChange={(e) => setForm({ ...form, scope_notes: e.target.value })} className="border border-neutral-300 px-3 py-2 text-sm" placeholder="Anything package-specific to spell out…" />
              </label>
              <div className="flex gap-2">
                <button type="button" onClick={saveAsTemplate} disabled={savingTemplate || !form.package_id} className="lf-btn-outline text-xs px-3 py-2 flex-1" data-testid="save-as-template-btn">
                  {savingTemplate ? "Saving…" : "Save as template"}
                </button>
                <button type="submit" data-testid="new-contract-submit" className="bg-neutral-900 text-white px-4 py-2.5 font-semibold hover:bg-neutral-700 flex-1 flex items-center justify-center gap-2">
                  <FileSignature className="w-4 h-4" /> Create draft
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  const border = accent === "emerald" ? "border-emerald-200 bg-emerald-50" : "border-neutral-200 bg-white";
  return (
    <div className={`border ${border} p-4`}>
      <div className="overline">{label}</div>
      <div className="text-2xl font-bold text-neutral-900 mt-1.5">{value ?? "—"}</div>
    </div>
  );
}

function PayoutToggle({ contractId, status, onChanged }) {
  const [busy, setBusy] = useState(false);
  const paid = status === "paid";
  const flip = async () => {
    setBusy(true);
    try {
      await api.post(`/contracts/${contractId}/${paid ? "mark-unpaid" : "mark-paid"}`);
      toast.success(paid ? "Marked unpaid." : "Payout recorded.");
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <button
      onClick={flip}
      disabled={busy}
      className={`text-xs px-3 py-1.5 flex items-center gap-1 border ${paid ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-neutral-300 text-neutral-700 hover:border-neutral-900"}`}
      data-testid={`contract-payout-${contractId}`}
    >
      <BadgeDollarSign className="w-3 h-3" /> {paid ? "Paid" : "Mark paid"}
    </button>
  );
}
